import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  Bold,
  Braces,
  Check,
  CircleAlert,
  EyeOff,
  Italic,
  List,
  ListOrdered,
  Loader2,
  MousePointerClick,
  RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorState } from "@/components/ui/states";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { VariavelDoCatalogo } from "@/components/finance/editor/EditorDeTextoRico";
import type { EdicaoDeTrecho } from "@/types/finance";

const ESPERA_PARA_SALVAR_MS = 700;
/** Largura de uma folha A4 em CSS px: a folha é montada nela e reduzida para caber na coluna, como no PDF. */
const LARGURA_DA_FOLHA = 794;

type EstadoDoSalvamento = "ocioso" | "salvando" | "salvo" | "erro";

interface TrechoAtivo {
  secaoId: string;
  titulo: string | null;
  editado: boolean;
}

interface ConteudoDoTrecho {
  titulo: string | null;
  corpo: string;
}

interface DocumentoEditavelProps {
  titulo: string;
  html: string | undefined;
  carregando: boolean;
  comErro: boolean;
  onRecarregar: () => void;
  editavel: boolean;
  variaveis?: VariavelDoCatalogo[];
  onSalvarTrecho: (secaoId: string, edicao: EdicaoDeTrecho) => Promise<unknown>;
  onRestaurarTrecho: (secaoId: string) => Promise<unknown>;
  onClicarParteAutomatica?: (bloco: string) => void;
  /** Controles acima da folha, à esquerda da barra de edição (ex.: o modelo do documento). */
  cabecalho?: ReactNode;
}

/**
 * O documento como ele sai, editável no lugar: o servidor marca cada trecho de
 * texto (`data-secao`, título e corpo em `data-campo`), os campos preenchidos
 * pelo sistema (`data-variavel`) e as partes automáticas (`data-bloco`). A
 * folha fica num iframe sem scripts; esta página liga a edição nos trechos,
 * guarda cada um ao parar de digitar e nunca troca o documento enquanto
 * alguém está escrevendo nele.
 */
export function DocumentoEditavel({
  titulo,
  html,
  carregando,
  comErro,
  onRecarregar,
  editavel,
  variaveis,
  onSalvarTrecho,
  onRestaurarTrecho,
  onClicarParteAutomatica,
  cabecalho,
}: DocumentoEditavelProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [htmlExibido, setHtmlExibido] = useState<string | undefined>(html);
  const htmlEmEspera = useRef<string | null>(null);
  const [altura, setAltura] = useState(900);
  const [escala, setEscala] = useState(1);
  const molduraRef = useRef<HTMLDivElement>(null);
  const [trechoAtivo, setTrechoAtivo] = useState<TrechoAtivo | null>(null);
  const [estado, setEstado] = useState<EstadoDoSalvamento>("ocioso");

  const originais = useRef(new Map<string, ConteudoDoTrecho>());
  const pendentes = useRef(new Set<string>());
  const salvandoAgora = useRef(0);
  const focoNoDocumento = useRef(false);
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selecaoGuardada = useRef<Range | null>(null);
  const observadorDeAltura = useRef<ResizeObserver | null>(null);

  const callbacks = useRef({
    onSalvarTrecho,
    onRestaurarTrecho,
    onClicarParteAutomatica,
  });
  callbacks.current = {
    onSalvarTrecho,
    onRestaurarTrecho,
    onClicarParteAutomatica,
  };

  const documento = () => iframeRef.current?.contentDocument ?? null;

  const ocupado = () => focoNoDocumento.current || pendentes.current.size > 0 || salvandoAgora.current > 0;

  const aplicarHtmlEmEspera = useCallback(() => {
    if (htmlEmEspera.current !== null && !ocupado()) {
      setHtmlExibido(htmlEmEspera.current);
      htmlEmEspera.current = null;
    }
  }, []);

  useEffect(() => {
    if (html === undefined) return;
    if (htmlExibido === undefined || !ocupado()) {
      setHtmlExibido(html);
      htmlEmEspera.current = null;
    } else if (html !== htmlExibido) {
      htmlEmEspera.current = html;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html]);

  const salvarPendentes = useCallback(async () => {
    if (temporizador.current) {
      clearTimeout(temporizador.current);
      temporizador.current = null;
    }
    const doc = documento();
    const ids = [...pendentes.current];
    pendentes.current.clear();
    if (!doc || ids.length === 0) {
      aplicarHtmlEmEspera();
      return;
    }
    salvandoAgora.current += 1;
    setEstado("salvando");
    try {
      for (const secaoId of ids) {
        const secao = doc.querySelector<HTMLElement>(`section[data-secao="${CSS.escape(secaoId)}"]`);
        if (!secao) continue;
        const atual = lerTrecho(secao);
        const original = originais.current.get(secaoId);
        const edicao: EdicaoDeTrecho = {};
        if (atual.titulo !== null && atual.titulo !== original?.titulo) edicao.titulo = atual.titulo;
        if (atual.corpo !== original?.corpo) edicao.html = atual.corpo;
        if (Object.keys(edicao).length === 0) continue;
        await callbacks.current.onSalvarTrecho(secaoId, edicao);
        originais.current.set(secaoId, atual);
        secao.setAttribute("data-editado", "");
      }
      setEstado("salvo");
      setTrechoAtivo((ativo) => (ativo && ids.includes(ativo.secaoId) ? { ...ativo, editado: true } : ativo));
    } catch {
      ids.forEach((secaoId) => pendentes.current.add(secaoId));
      setEstado("erro");
    } finally {
      salvandoAgora.current -= 1;
      aplicarHtmlEmEspera();
    }
  }, [aplicarHtmlEmEspera]);

  const salvarPendentesRef = useRef(salvarPendentes);
  salvarPendentesRef.current = salvarPendentes;
  useEffect(() => () => void salvarPendentesRef.current(), []);

  const prepararDocumento = useCallback(() => {
    const doc = documento();
    if (!doc) return;
    originais.current.clear();
    pendentes.current.clear();
    setTrechoAtivo(null);

    doc.querySelectorAll<HTMLElement>("section[data-secao]").forEach((secao) => {
      originais.current.set(secao.dataset.secao ?? "", lerTrecho(secao));
    });

    observadorDeAltura.current?.disconnect();
    const medir = () => setAltura(Math.max(doc.documentElement.scrollHeight, 400));
    medir();
    observadorDeAltura.current = new ResizeObserver(medir);
    observadorDeAltura.current.observe(doc.body);

    if (!editavel) return;
    doc.querySelectorAll<HTMLElement>("[data-campo]").forEach((campo) => {
      campo.contentEditable = "true";
      campo.spellcheck = true;
    });
    try {
      doc.execCommand("defaultParagraphSeparator", false, "p");
    } catch {
      // navegador sem suporte: o Enter cria <div>, que lerTrecho converte em <p>
    }

    const secaoDe = (alvo: EventTarget | null) =>
      alvo instanceof doc.defaultView!.Node
        ? (((alvo as Node).nodeType === 1 ? (alvo as Element) : (alvo as Node).parentElement)?.closest<HTMLElement>(
            "section[data-secao]",
          ) ?? null)
        : null;

    doc.addEventListener("focusin", (evento) => {
      const secao = secaoDe(evento.target);
      if (!secao) return;
      focoNoDocumento.current = true;
      setTrechoAtivo({
        secaoId: secao.dataset.secao ?? "",
        titulo: secao.querySelector("[data-campo='titulo']")?.textContent?.trim() || null,
        editado: secao.hasAttribute("data-editado"),
      });
    });
    doc.addEventListener("focusout", (evento) => {
      if (secaoDe(evento.relatedTarget)) return;
      focoNoDocumento.current = false;
      void salvarPendentesRef.current();
    });
    doc.addEventListener("input", (evento) => {
      const secao = secaoDe(evento.target);
      if (!secao) return;
      pendentes.current.add(secao.dataset.secao ?? "");
      setEstado("ocioso");
      if (temporizador.current) clearTimeout(temporizador.current);
      temporizador.current = setTimeout(() => void salvarPendentesRef.current(), ESPERA_PARA_SALVAR_MS);
    });
    doc.addEventListener("paste", (evento) => {
      if (!secaoDe(evento.target)) return;
      evento.preventDefault();
      const texto = evento.clipboardData?.getData("text/plain") ?? "";
      doc.execCommand("insertText", false, texto);
    });
    doc.addEventListener("keydown", (evento) => {
      const alvo = evento.target as HTMLElement | null;
      if (evento.key === "Enter" && alvo?.dataset?.campo === "titulo") evento.preventDefault();
    });
    doc.addEventListener("selectionchange", () => {
      const selecao = doc.getSelection();
      if (selecao && selecao.rangeCount > 0 && secaoDe(selecao.anchorNode)) {
        selecaoGuardada.current = selecao.getRangeAt(0).cloneRange();
      }
    });
    doc.addEventListener("click", (evento) => {
      const alvo = evento.target as Element | null;
      const removida = alvo?.closest<HTMLElement>("[data-secao-removida]");
      if (removida) {
        void callbacks.current.onRestaurarTrecho(removida.dataset.secaoRemovida ?? "");
        return;
      }
      const bloco = alvo?.closest<HTMLElement>("[data-bloco]");
      if (bloco) callbacks.current.onClicarParteAutomatica?.(bloco.dataset.bloco ?? "");
    });
  }, [editavel]);

  useEffect(() => () => observadorDeAltura.current?.disconnect(), []);

  useEffect(() => {
    const moldura = molduraRef.current;
    if (!moldura) return;
    const observador = new ResizeObserver(([entrada]) =>
      setEscala(Math.min(1, entrada.contentRect.width / LARGURA_DA_FOLHA)),
    );
    observador.observe(moldura);
    return () => observador.disconnect();
  }, []);

  const comando = (nome: string, valor?: string) => {
    const doc = documento();
    if (!doc) return;
    restaurarSelecao(doc, selecaoGuardada.current);
    doc.execCommand(nome, false, valor);
  };

  /** Pelo Range e não por insertHTML: no fim de um parágrafo o Chrome joga um elemento não editável para fora dele. */
  const inserirCampo = (variavel: VariavelDoCatalogo) => {
    const doc = documento();
    if (!doc) return;
    restaurarSelecao(doc, selecaoGuardada.current);
    const selecao = doc.getSelection();
    if (!selecao || selecao.rangeCount === 0) return;
    const range = selecao.getRangeAt(0);
    const campo = doc.createElement("span");
    campo.className = "campo";
    campo.dataset.variavel = variavel.nome;
    campo.contentEditable = "false";
    campo.textContent = `[${variavel.rotulo}]`;
    range.deleteContents();
    range.insertNode(campo);
    const espaco = doc.createTextNode("\u00a0");
    campo.after(espaco);
    range.setStartAfter(espaco);
    range.collapse(true);
    selecao.removeAllRanges();
    selecao.addRange(range);
    campo.dispatchEvent(new (doc.defaultView ?? window).Event("input", { bubbles: true }));
  };

  const tirarDoDocumento = async () => {
    if (!trechoAtivo) return;
    pendentes.current.delete(trechoAtivo.secaoId);
    focoNoDocumento.current = false;
    const secaoId = trechoAtivo.secaoId;
    setTrechoAtivo(null);
    setEstado("salvando");
    try {
      await callbacks.current.onSalvarTrecho(secaoId, { removida: true });
      setEstado("salvo");
    } catch {
      setEstado("erro");
    }
  };

  const voltarAoModelo = async () => {
    if (!trechoAtivo) return;
    pendentes.current.delete(trechoAtivo.secaoId);
    focoNoDocumento.current = false;
    const secaoId = trechoAtivo.secaoId;
    setTrechoAtivo(null);
    setEstado("salvando");
    try {
      await callbacks.current.onRestaurarTrecho(secaoId);
      setEstado("salvo");
    } catch {
      setEstado("erro");
    }
  };

  const manterFoco = (evento: React.MouseEvent) => evento.preventDefault();
  const semTrecho = !trechoAtivo;

  return (
    <div className="space-y-3">
      <div className="sticky top-0 z-20 -mx-1 flex flex-wrap items-center gap-2 rounded-lg border bg-background/95 px-2 py-1.5 shadow-xs backdrop-blur-sm">
        {cabecalho}
        {editavel && (
          <>
            {cabecalho && <Separator orientation="vertical" className="mx-1 h-5" />}
            <div className="flex items-center gap-0.5" onMouseDown={manterFoco}>
              <BotaoDaBarra rotulo="Negrito" disabled={semTrecho} onClick={() => comando("bold")}>
                <Bold className="h-3.5 w-3.5" />
              </BotaoDaBarra>
              <BotaoDaBarra rotulo="Itálico" disabled={semTrecho} onClick={() => comando("italic")}>
                <Italic className="h-3.5 w-3.5" />
              </BotaoDaBarra>
              <BotaoDaBarra
                rotulo="Lista com marcadores"
                disabled={semTrecho}
                onClick={() => comando("insertUnorderedList")}
              >
                <List className="h-3.5 w-3.5" />
              </BotaoDaBarra>
              <BotaoDaBarra rotulo="Lista numerada" disabled={semTrecho} onClick={() => comando("insertOrderedList")}>
                <ListOrdered className="h-3.5 w-3.5" />
              </BotaoDaBarra>
            </div>
            {variaveis && variaveis.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 gap-1 px-2 text-xs"
                    disabled={semTrecho}
                  >
                    <Braces className="h-3.5 w-3.5" />
                    Inserir campo
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="max-h-80 w-72 overflow-y-auto"
                  onCloseAutoFocus={(evento) => evento.preventDefault()}
                >
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    Preenchido com os dados do documento
                  </DropdownMenuLabel>
                  {variaveis.map((variavel) => (
                    <DropdownMenuItem
                      key={variavel.nome}
                      onSelect={() => inserirCampo(variavel)}
                      className="flex flex-col items-start gap-0"
                    >
                      <span className="text-sm">{variavel.rotulo}</span>
                      {variavel.exemplo && (
                        <span className="text-[11px] text-muted-foreground">Ex.: {variavel.exemplo}</span>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <div className="flex items-center gap-0.5" onMouseDown={manterFoco}>
              {trechoAtivo?.editado && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 gap-1 px-2 text-xs"
                  onClick={voltarAoModelo}
                  title="Desfaz as edições deste trecho nesta proposta"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Voltar ao texto do modelo
                </Button>
              )}
              {trechoAtivo && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 gap-1 px-2 text-xs hover:text-destructive"
                  onClick={tirarDoDocumento}
                >
                  <EyeOff className="h-3.5 w-3.5" />
                  Tirar do documento
                </Button>
              )}
            </div>
            <span className="ml-auto flex items-center gap-1.5 pr-1 text-xs text-muted-foreground">
              <IndicadorDeSalvamento estado={estado} trechoAtivo={trechoAtivo} />
            </span>
          </>
        )}
      </div>

      <div ref={molduraRef} className="rounded-md bg-muted/40 p-2 sm:p-4">
        {carregando && !htmlExibido ? (
          <Skeleton className="mx-auto h-[80vh] w-full max-w-[794px]" />
        ) : comErro && !htmlExibido ? (
          <ErrorState title="Não foi possível montar o documento" onRetry={onRecarregar} className="p-6" />
        ) : (
          <div
            className="mx-auto overflow-hidden rounded-sm bg-white shadow-md ring-1 ring-black/5"
            style={{ width: LARGURA_DA_FOLHA * escala, height: altura * escala }}
          >
            <iframe
              ref={iframeRef}
              title={titulo}
              srcDoc={htmlExibido}
              sandbox="allow-same-origin"
              onLoad={prepararDocumento}
              style={{
                width: LARGURA_DA_FOLHA,
                height: altura,
                transform: `scale(${escala})`,
                transformOrigin: "0 0",
              }}
              className="block border-0 bg-white"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function IndicadorDeSalvamento({
  estado,
  trechoAtivo,
}: {
  estado: EstadoDoSalvamento;
  trechoAtivo: TrechoAtivo | null;
}) {
  if (estado === "salvando") {
    return (
      <>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Salvando
      </>
    );
  }
  if (estado === "erro") {
    return (
      <span className="flex items-center gap-1 text-destructive">
        <CircleAlert className="h-3.5 w-3.5" />
        Não salvou. Continue editando para tentar de novo.
      </span>
    );
  }
  if (estado === "salvo" && !trechoAtivo) {
    return (
      <>
        <Check className="h-3.5 w-3.5 text-emerald-600" />
        Salvo nesta proposta
      </>
    );
  }
  if (trechoAtivo) {
    return <span className="max-w-[16rem] truncate">Editando: {trechoAtivo.titulo ?? "texto sem título"}</span>;
  }
  return (
    <>
      <MousePointerClick className="h-3.5 w-3.5" />
      Clique num texto do documento para editar
    </>
  );
}

function BotaoDaBarra({
  children,
  rotulo,
  disabled,
  onClick,
}: {
  children: ReactNode;
  rotulo: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      size="icon"
      variant="ghost"
      className="h-7 w-7"
      aria-label={rotulo}
      title={rotulo}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

/**
 * Título e corpo de um trecho como o servidor espera: campos de volta a
 * `<span data-variavel="NOME"></span>` vazio e texto solto ou `<div>` (o que o
 * navegador cria ao apertar Enter) virando parágrafo. O resto da limpeza é o
 * allowlist do servidor.
 */
function lerTrecho(secao: HTMLElement): ConteudoDoTrecho {
  const titulo = secao.querySelector<HTMLElement>("[data-campo='titulo']");
  const corpo = secao.querySelector<HTMLElement>("[data-campo='corpo']");
  return {
    titulo: titulo ? (titulo.textContent ?? "").replace(/\s+/g, " ").trim() : null,
    corpo: corpo ? normalizarCorpo(corpo) : "",
  };
}

function normalizarCorpo(corpo: HTMLElement): string {
  const copia = corpo.cloneNode(true) as HTMLElement;
  const doc = copia.ownerDocument;
  copia.querySelectorAll<HTMLElement>("[data-variavel]").forEach((campo) => {
    const vazio = doc.createElement("span");
    vazio.setAttribute("data-variavel", campo.dataset.variavel ?? "");
    campo.replaceWith(vazio);
  });
  copia.querySelectorAll<HTMLElement>("[style]").forEach((elemento) => elemento.removeAttribute("style"));

  const resultado = doc.createElement("div");
  let paragrafoAberto: HTMLElement | null = null;
  const BLOCOS = new Set(["P", "UL", "OL", "H3", "H4", "BLOCKQUOTE"]);
  [...copia.childNodes].forEach((no) => {
    const elemento = no.nodeType === 1 ? (no as HTMLElement) : null;
    if (elemento && elemento.tagName === "DIV") {
      const paragrafo = doc.createElement("p");
      while (elemento.firstChild) paragrafo.appendChild(elemento.firstChild);
      resultado.appendChild(paragrafo);
      paragrafoAberto = null;
      return;
    }
    if (elemento && BLOCOS.has(elemento.tagName)) {
      resultado.appendChild(elemento);
      paragrafoAberto = null;
      return;
    }
    if (!elemento && !(no.textContent ?? "").trim()) return;
    if (!paragrafoAberto) {
      paragrafoAberto = doc.createElement("p");
      resultado.appendChild(paragrafoAberto);
    }
    paragrafoAberto.appendChild(no);
  });
  return resultado.innerHTML;
}

function restaurarSelecao(doc: Document, range: Range | null) {
  const selecao = doc.getSelection();
  if (!selecao || !range || !range.startContainer.isConnected) return;
  const editavel = (
    range.startContainer.nodeType === 1 ? (range.startContainer as HTMLElement) : range.startContainer.parentElement
  )?.closest<HTMLElement>("[data-campo]");
  editavel?.focus();
  selecao.removeAllRanges();
  selecao.addRange(range);
}
